import {
  FlexboxJustify,
  HStack,
  Hide,
  Icon,
  Show,
  Text,
  TextProps,
  ValueByDevice,
  FlexboxAlign,
} from 'platform/foundation';

import {Fragment, ReactNode} from 'react';

import {reject} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty, isPositive} from 'ramda-adjunct';

import {getRandomId, DOT_CHARACTER, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {Tooltip} from '../Tooltip/Tooltip';
import {ParametersItem} from './ParametersItem';
import {Parameter} from './types/Parameter';
import {isCustomParameter} from './utils/isCustomParameter';

export interface ParametersProps extends TestIdProps {
  parameters: Parameter[] | Nullish;
  parametersProps?: TextProps;
  color?: 'primary' | 'secondary' | 'tertiary' | 'danger' | 'white';
  size?: 'base' | 'small' | 'xSmall';
  alternative?: boolean;
  justify?: FlexboxJustify | ValueByDevice<FlexboxJustify>;
  align?: FlexboxAlign | ValueByDevice<FlexboxAlign>;
  appendix?: ReactNode;
}

export function Parameters(props: ParametersProps) {
  const size = props.size ?? 'base';
  const filteredParameters = reject(isNilOrEmpty, props.parameters ?? []);
  if (!filteredParameters?.length) {
    return null;
  }

  return (
    <HStack
      spacing={1}
      data-testid={props['data-testid']}
      wrap
      align={props.align}
      justify={props.justify}
    >
      {filteredParameters.map((parameter, index) => {
        const isCustom = isCustomParameter(parameter);
        const hasAutoWidth = isCustom ? parameter.hasAutoWidth : false;
        const shouldShowTooltipIcon = isCustom ? parameter.shouldShowTooltipIcon : false;
        const hideSeparator = isCustom ? parameter.hideSeparator : false;
        const title = isCustom ? parameter.title : parameter;
        const tooltip = isCustom ? parameter.tooltip : null;

        return (
          <Fragment key={`${title}_${getRandomId()}`}>
            <Show when={isPositive(index) && !hideSeparator}>
              <Text color="tertiary" size={size}>
                {DOT_CHARACTER}
              </Text>
            </Show>

            <Hide when={shouldShowTooltipIcon}>
              <Tooltip label={tooltip}>
                {/* Not using <Show> component because of incorrect type guard */}
                {isCustom ? (
                  <ParametersItem
                    data-testid={suffixTestId(`parameter-[${index.toString()}]`, props)}
                    title={title}
                    content={parameter.content}
                    size={size}
                    variant={parameter.variant}
                    icon={parameter.icon}
                    isIconNotFilled={parameter.isIconNotFilled}
                    alternative={props.alternative}
                    parametersProps={props.parametersProps}
                    onClick={parameter.onClick}
                  />
                ) : (
                  <Text
                    data-testid={suffixTestId(`parameter-[${index.toString()}]`, props)}
                    color={props.color}
                    size={size}
                    noWrap
                    alternative={props.alternative}
                    {...props.parametersProps}
                  >
                    {title}
                  </Text>
                )}
              </Tooltip>
            </Hide>

            <Show when={shouldShowTooltipIcon}>
              <HStack align="center" spacing={1}>
                {/* Not using <Show> component because of incorrect type guard */}
                {isCustom ? (
                  <ParametersItem
                    data-testid={props['data-testid']}
                    title={title}
                    content={parameter.content}
                    size={size}
                    variant={parameter.variant}
                    icon={parameter.icon}
                    isIconNotFilled={parameter.isIconNotFilled}
                    alternative={props.alternative}
                    parametersProps={props.parametersProps}
                    onClick={parameter.onClick}
                  />
                ) : (
                  <Text
                    data-testid={suffixTestId(`parameter-[${index.toString()}]`, props)}
                    color={props.color}
                    size={size}
                    noWrap
                    alternative={props.alternative}
                    {...props.parametersProps}
                  >
                    {title}
                  </Text>
                )}

                <Show when={isNotNilOrEmpty(tooltip)}>
                  <Tooltip placement="top" description={tooltip} hasAutoWidth={hasAutoWidth}>
                    <Icon value="action/help" size={4} color="text.secondary" />
                  </Tooltip>
                </Show>
              </HStack>
            </Show>
          </Fragment>
        );
      })}
      {props.appendix}
    </HStack>
  );
}
